import { AppException, AppErrorCode } from '@/shared/types/errors';
import { auth, db } from '@/services/firebase';
import {
    collection,
    doc,
    addDoc,
    getDoc,
    getDocs,
    query,
    orderBy,
    limit,
    serverTimestamp,
    updateDoc
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useStore } from '@/core/store';
import { functions } from '@/services/firebase';
import { LegalContract, ContractStatus } from '@/modules/legal/types';
import { rightsIntelligenceService } from '@/services/rights/RightsIntelligenceService';
import {
    CanonicalClaimsInboxSnapshotSchema,
    CanonicalDeclaredClaimResultSchema,
    type ClaimsInboxProjection,
    type ClaimsInboxInput,
    type CanonicalClaimsInboxSnapshot,
    type CanonicalDeclaredClaimResult,
    type RightsClaim,
} from '@indii/shared';


export interface ContractAnalysis {
    id?: string;
    fileName: string;
    score: number;
    summary: string;
    risks: string[];
    analyzedAt: string;
}

// Lazy load to break agent dependencies from UI layer if not directly used
let LegalToolsModule: typeof import('@/services/agent/tools/LegalTools') | null = null;
async function getLegalTools() {
    if (!LegalToolsModule) {
        LegalToolsModule = await import('@/services/agent/tools/LegalTools');
    }
    return LegalToolsModule.LegalTools;
}

export class LegalService {

    /**
     * Resolve the active user ID for legal operations.
     * Prefers verified userProfile.id (if not 'pending'), then active Firebase Auth identity.
     * Rejects placeholder 'pending' state and unauthenticated callers.
     */
    static resolveUserId(): string {
        const store = useStore.getState();
        if (store.userProfile === null && !store.user) {
            throw new AppException(AppErrorCode.AUTH_ERROR, 'User not authenticated');
        }

        const profileId = store.userProfile?.id;
        if (profileId && profileId !== 'pending') {
            return profileId;
        }

        const authUid = auth.currentUser?.uid || store.user?.uid;
        if (authUid && authUid !== 'pending') {
            return authUid;
        }

        throw new AppException(AppErrorCode.AUTH_ERROR, 'User not authenticated');
    }

    /**
     * Save a new contract draft
     */
    static async saveContract(data: Omit<LegalContract, 'id' | 'userId' | 'createdAt' | 'updatedAt'>): Promise<string> {
        const userId = this.resolveUserId();

        const contractData = {
            ...data,
            userId,
            status: data.status || ContractStatus.DRAFT,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        };

        const docRef = await addDoc(collection(db, 'users', userId, 'contracts'), contractData);
        return docRef.id;
    }

    /**
     * Update an existing contract
     */
    static async updateContract(id: string, updates: Partial<LegalContract>): Promise<void> {
        const userId = this.resolveUserId();
        const docRef = doc(db, 'users', userId, 'contracts', id);
        await updateDoc(docRef, { ...updates, updatedAt: serverTimestamp() });
    }

    /**
     * Get all contracts for the current user
     */
    static async getContracts(): Promise<LegalContract[]> {
        // The authenticated shell can mount before the presentation profile loads.
        // Use the resolved identity, never the profile's initial "pending" ID.
        let userId: string;
        try {
            userId = this.resolveUserId();
        } catch {
            return [];
        }

        const q = query(
            collection(db, 'users', userId, 'contracts'),
            orderBy('updatedAt', 'desc')
        );

        const snapshot = await getDocs(q);
        return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as unknown as LegalContract));
    }

    /**
     * Get a single contract by ID
     */
    static async getContractById(id: string): Promise<LegalContract | null> {
        let userId: string;
        try {
            userId = this.resolveUserId();
        } catch {
            return null;
        }

        const snapshot = await getDoc(doc(db, 'users', userId, 'contracts', id));
        return snapshot.exists()
            ? ({ id: snapshot.id, ...snapshot.data() } as unknown as LegalContract)
            : null;
    }

    // -----------------------------------------------------------------------
    // Contract Analysis persistence
    // -----------------------------------------------------------------------

    /**
     * Persist an Intelligence contract analysis result to Firestore.
     * Stored under users/{uid}/contract_analyses.
     */
    static async saveAnalysis(analysis: Omit<ContractAnalysis, 'id'>): Promise<string> {
        let userId: string;
        try {
            userId = this.resolveUserId();
        } catch {
            // Silently skip if unauthenticated — analysis results are still shown in UI
            return '';
        }

        const docRef = await addDoc(
            collection(db, 'users', userId, 'contract_analyses'),
            { ...analysis, savedAt: serverTimestamp() }
        );
        return docRef.id;
    }

    /**
     * Fetch the 20 most recent contract analyses for the current user.
     */
    static async getAnalyses(): Promise<ContractAnalysis[]> {
        let userId: string;
        try {
            userId = this.resolveUserId();
        } catch {
            return [];
        }

        const q = query(
            collection(db, 'users', userId, 'contract_analyses'),
            orderBy('savedAt', 'desc'),
            limit(20)
        );

        const snapshot = await getDocs(q);
        return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as unknown as ContractAnalysis));
    }

    // -----------------------------------------------------------------------
    // Agent Tool Wrappers (Decoupling UI from direct Agent invocations)
    // -----------------------------------------------------------------------

    /**
     * Generate an NDA via Autonomous Agent 
     */
    static async generateNDA(parties: string[], purpose: string) {
        const tools = await getLegalTools();
        if (!tools.generate_nda) throw new Error('Tool generate_nda is missing');
        return tools.generate_nda({ parties, purpose });
    }

    /**
     * Draft an arbitrary contract via Autonomous Agent
     */
    static async draftContract(type: string, parties: string[], terms: string) {
        const tools = await getLegalTools();
        if (!tools.draft_contract) throw new Error('Tool draft_contract is missing');
        return tools.draft_contract({ type, parties, terms });
    }

    // -----------------------------------------------------------------------
    // Claims Inbox (Phase 12 Operational Gate / Phase 20 Surface)
    // -----------------------------------------------------------------------

    /**
     * Projects the advisory claims inbox view for the user.
     * Uses platform-neutral canonical claim records without altering ownership.
     */
    static getClaimsInbox(input?: Partial<ClaimsInboxInput>): ClaimsInboxProjection {
        const claims = input?.claims || [];
        const events = input?.events || [];
        const evaluatedAt = input?.evaluatedAt || new Date().toISOString();
        return rightsIntelligenceService.projectClaims({
            claims,
            events,
            evaluatedAt,
        });
    }

    static async loadCanonicalClaimsInbox(): Promise<CanonicalClaimsInboxSnapshot> {
        const userId = useStore.getState().userProfile?.id;
        if (!userId) throw new AppException(AppErrorCode.AUTH_ERROR, 'Sign in to load the claims inbox.');
        if (!functions) throw new Error('Claims inbox service is unavailable.');
        const read = httpsCallable<{ scope: { kind: 'user'; id: string } }, unknown>(functions, 'getCanonicalClaimsInbox');
        const response = await read({ scope: { kind: 'user', id: userId } });
        return CanonicalClaimsInboxSnapshotSchema.parse(response.data);
    }

    static async declareCanonicalRightsClaim(input: {
        targetEntityId: string;
        claimantEntityId?: string;
        type: RightsClaim['type'];
        territoryCodes: string[];
        evidence?: RightsClaim['provenance']['evidence'];
        note?: string;
    }): Promise<CanonicalDeclaredClaimResult> {
        const userId = useStore.getState().userProfile?.id;
        if (!userId) throw new AppException(AppErrorCode.AUTH_ERROR, 'Sign in to record a claim.');
        if (!functions) throw new Error('Claims inbox service is unavailable.');
        const create = httpsCallable<typeof input & { scope: { kind: 'user'; id: string } }, unknown>(
            functions,
            'declareCanonicalRightsClaim',
        );
        const response = await create({ ...input, scope: { kind: 'user', id: userId } });
        return CanonicalDeclaredClaimResultSchema.parse(response.data);
    }

    static async respondToCanonicalRightsClaim(input: {
        claimId: string;
        status: 'ASSERTED' | 'DISPUTED' | 'WITHDRAWN';
        note?: string;
    }): Promise<CanonicalDeclaredClaimResult> {
        const userId = useStore.getState().userProfile?.id;
        if (!userId) throw new AppException(AppErrorCode.AUTH_ERROR, 'Sign in to respond to a claim.');
        if (!functions) throw new Error('Claims inbox service is unavailable.');
        const respond = httpsCallable<typeof input & { scope: { kind: 'user'; id: string } }, unknown>(
            functions,
            'respondToCanonicalRightsClaim',
        );
        const response = await respond({ ...input, scope: { kind: 'user', id: userId } });
        return CanonicalDeclaredClaimResultSchema.parse(response.data);
    }
}
